import { invoke } from '@tauri-apps/api/core'
import { listen } from '@tauri-apps/api/event'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { getOfflineStyleCacheKey, type MapStylePreference } from '@/config/mapStyle'
import { apiService, type WorkspaceSearchItem } from '@/services/apiService'
import { resolveApiBaseUrl } from '@/services/apiUrlService'
import { connectivityService } from '@/services/connectivityService'
import { buildLineStringFromSegments } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { getActiveOfflineOwnerUserId } from '@/services/offlineAuthBridge'
import {
  assertManualMapEstimateWithinLimits,
  type OfflineCoverageCell,
  type OfflineEstimate,
  type TileSourceInfo,
  computeWorkspaceBbox,
  discoverTileSources,
  enumerateCoverageTiles,
  enumerateTiles,
  buildTileJobs,
  estimateCoverageSize,
  estimateOfflineSize,
  MANUAL_MAP_DISPLAY_NAME,
  MANUAL_MAP_MAX_TILE_JOBS,
  MANUAL_MAP_PACKAGE_ID,
  normalizeCoverageCells,
  selectTileSourcesForDownload,
  OFFLINE_MAX_ZOOM,
} from '@/services/offlineTilesService'
import { isTauri } from '@/services/platformService'
import { settingsService } from '@/services/settingsService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import type { Photo } from '@/types/photo'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour, TourListItem } from '@/types/tour'
import type { Workspace } from '@/types/workspace'
import type {
  OfflineGeometryBundle,
  OfflineWorkspaceSnapshot,
  WorkspaceDetailPayload,
} from '@/types/workspaceDetail'

export type OfflineStatus = 'ready' | 'partial' | 'downloading' | 'error'
export type OfflinePackageKind = 'workspace' | 'map_area'

export interface OfflineEntry {
  workspaceId: string
  packageKind: OfflinePackageKind
  workspaceName: string | null
  coverageCells: OfflineCoverageCell[]
  includeTerrain: boolean
  sourceIds: string[]
  sourceExtensions: Record<string, string>
  sourceMinzooms: Record<string, number>
  sourceMaxzooms: Record<string, number>
  status: OfflineStatus
  downloadedAt: number
  sizeBytes: number
  tileCount: number
  photoCount: number
  photosIncluded: boolean
  styleId: string
  workspaceHash: string
}

export interface ActiveDownload {
  phase: 'waiting' | 'meta' | 'tiles' | 'photos'
  done: number
  total: number
  overallDone: number
  overallTotal: number
  error?: string
  workspaceName?: string | null
  styleId?: string
  photosIncluded?: boolean
  photoCount?: number
}

export interface OfflineDownloadOptions {
  includePhotos: boolean
  includeTerrain: boolean
}

export interface PendingDownloadInput {
  owner_user_id: number
  workspace_id: string
  package_kind?: OfflinePackageKind
  display_name?: string | null
  coverage_cells?: OfflineCoverageCell[]
  include_terrain?: boolean
  style_id: string
  workspace_hash: string
  api_base_url?: string
  meta_json: unknown
  tile_sources: DownloadTileSourceJob[]
  photo_jobs: DownloadPhotoJob[]
  include_photos: boolean
  paused?: boolean
  last_error?: string | null
}

export interface PendingOfflineDownload {
  workspaceId: string
  input: PendingDownloadInput
  workspaceName: string | null
  styleId: string
  photosIncluded: boolean
  photoCount: number
  paused: boolean
  lastError: string | null
}

interface PendingDownloadSnapshot {
  epoch: number
  downloads: Record<string, PendingOfflineDownload>
}

interface ActiveDownloadMetadata {
  workspaceName?: string | null
  styleId?: string
  photosIncluded?: boolean
  photoCount?: number
}

interface SizeStatsPayload {
  sizeBytes?: number
  size_bytes?: number
  tileCount?: number
  tile_count?: number
  photoCount?: number
  photo_count?: number
}

interface TileCacheStatsPayload {
  bytes?: number
  entries?: number
}

interface OfflineEntryPayload {
  ownerUserId?: number
  owner_user_id?: number
  workspaceId?: string
  workspace_id?: string
  packageKind?: OfflinePackageKind
  package_kind?: OfflinePackageKind
  workspaceName?: string | null
  workspace_name?: string | null
  coverageCells?: OfflineCoverageCell[]
  coverage_cells?: OfflineCoverageCell[]
  includeTerrain?: boolean
  include_terrain?: boolean
  sourceIds?: string[]
  source_ids?: string[]
  status?: OfflineStatus
  downloadedAt?: number
  downloaded_at?: number
  sizeBytes?: number
  size_bytes?: number
  tileCount?: number
  tile_count?: number
  photoCount?: number
  photo_count?: number
  photosIncluded?: boolean
  photos_included?: boolean
  styleId?: string
  style_id?: string
  workspaceHash?: string
  workspace_hash?: string
  sourceExtensions?: Record<string, string>
  source_extensions?: Record<string, string>
  sourceMinzooms?: Record<string, number>
  source_minzooms?: Record<string, number>
  sourceMaxzooms?: Record<string, number>
  source_maxzooms?: Record<string, number>
}

interface OfflineIndexPayload {
  version?: number
  ownerUserId?: number
  owner_user_id?: number
  workspaces?: Record<string, OfflineEntryPayload>
}

interface DownloadTileJob {
  z: number
  x: number
  y: number
  url: string
  ext: string
}

interface DownloadTileSourceJob {
  source_id: string
  tiles: DownloadTileJob[]
}

interface DownloadPhotoJob {
  photo_id: string
  url: string
  thumbnail_url: string | null
  ext: string
}

interface PendingDownloadsResponse {
  ownerUserId?: number
  owner_user_id?: number
  downloads: PendingDownloadInput[]
}

interface OfflineWorkspaceSnapshotPayload {
  version?: number
  workspace_detail?: WorkspaceDetailPayload
  workspaceDetail?: WorkspaceDetailPayload
  search_item?: WorkspaceSearchItem
  searchItem?: WorkspaceSearchItem
  library_tours?: TourListItem[]
  libraryTours?: TourListItem[]
  geometry_by_resolution?: Partial<Record<'50m' | '5m', OfflineGeometryBundle>>
  geometryByResolution?: Partial<Record<'50m' | '5m', OfflineGeometryBundle>>
}

interface ProgressEvent {
  owner_user_id: number
  workspace_id: string
  phase: 'waiting' | 'meta' | 'tiles' | 'photos'
  done: number
  total: number
}

interface OfflineWorkspaceEvent {
  owner_user_id: number
  workspace_id: string
}

const LARGE_DOWNLOAD_BYTES_THRESHOLD = 1_000_000_000

export const useOfflineStore = defineStore('offline', () => {
  const index = ref<Record<string, OfflineEntry>>({})
  const active = ref<Record<string, ActiveDownload>>({})
  const pending = ref<Record<string, PendingOfflineDownload>>({})
  const deleting = ref<Record<string, boolean>>({})
  const initialized = ref(false)
  // Opportunistic map-tile HTTP cache (native `rctiles` proxy, app cache dir).
  // Separate from the durable offline packages above — see tile_cache.rs.
  const tileCacheBytes = ref(0)
  const tileCacheEntries = ref(0)
  const ownerUserId = ref<number | null>(getActiveOfflineOwnerUserId())
  const listenersRegistered = ref(false)
  const deletePromises = new Map<string, Promise<void>>()
  const locallyChangedPendingIds = new Set<string>()
  let pendingEpoch = 0
  let pendingHydrated = false
  let pendingLoadPromise: Promise<PendingDownloadSnapshot> | null = null
  let initializationPromise: Promise<void> | null = null

  const isOffline = computed(() => (workspaceId: string) => workspaceId in index.value)

  const needsUpdate = computed(() => (workspaceId: string, currentHash: string) => {
    const entry = index.value[workspaceId]
    if (!entry) return false
    return entry.workspaceHash !== currentHash
  })

  const needsStyleUpdate = computed(() => (workspaceId: string, preference: MapStylePreference) => {
    const entry = index.value[workspaceId]
    if (!entry) return false
    return entry.styleId !== getOfflineStyleCacheKey(preference)
  })

  const totalSize = computed(() =>
    Object.values(index.value).reduce((sum, e) => sum + e.sizeBytes, 0),
  )

  const readyWorkspaceIds = computed(() =>
    Object.values(index.value)
      .filter((entry) => entry.status === 'ready' && entry.packageKind === 'workspace')
      .map((entry) => entry.workspaceId),
  )

  const manualMapEntry = computed(() => index.value[MANUAL_MAP_PACKAGE_ID] ?? null)

  function getEntry(workspaceId: string): OfflineEntry | null {
    return index.value[workspaceId] ?? null
  }

  function getStyleCacheKey(preference: MapStylePreference): string {
    return getOfflineStyleCacheKey(preference)
  }

  async function readWorkspaceSnapshot(
    workspaceId: string,
  ): Promise<OfflineWorkspaceSnapshot | null> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return null

    try {
      const payload = await invoke<OfflineWorkspaceSnapshotPayload>(
        'read_offline_workspace_snapshot',
        {
          workspaceId,
        },
      )
      if (ownerUserId.value !== expectedOwnerUserId) return null
      const workspaceDetail = payload.workspaceDetail ?? payload.workspace_detail
      if (!workspaceDetail) {
        return null
      }

      return {
        version: payload.version ?? 1,
        workspaceDetail,
        searchItem: payload.searchItem ?? payload.search_item,
        libraryTours: payload.libraryTours ?? payload.library_tours,
        geometryByResolution: payload.geometryByResolution ?? payload.geometry_by_resolution,
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to read offline workspace snapshot', {
        workspaceId,
        error,
      })
      return null
    }
  }

  function normalizeSizeStats(payload: SizeStatsPayload): {
    sizeBytes: number
    tileCount: number
    photoCount: number
  } {
    return {
      sizeBytes: payload.sizeBytes ?? payload.size_bytes ?? 0,
      tileCount: payload.tileCount ?? payload.tile_count ?? 0,
      photoCount: payload.photoCount ?? payload.photo_count ?? 0,
    }
  }

  function buildActiveDownloadState(
    phase: ActiveDownload['phase'],
    done: number,
    total: number,
    options: {
      error?: string
      metadata?: ActiveDownloadMetadata
    } = {},
  ): ActiveDownload {
    const overallTotal = Math.max(1, total)
    const overallDone = Math.min(Math.max(done, 0), overallTotal)

    return {
      phase,
      done,
      total,
      overallDone,
      overallTotal,
      error: options.error,
      workspaceName: options.metadata?.workspaceName ?? null,
      styleId: options.metadata?.styleId ?? '',
      photosIncluded: options.metadata?.photosIncluded ?? false,
      photoCount: options.metadata?.photoCount ?? 0,
    }
  }

  function extractWorkspaceNameFromMeta(metaJson: unknown): string | null {
    if (!metaJson || typeof metaJson !== 'object') return null

    const candidates = [
      (metaJson as { workspace_detail?: unknown }).workspace_detail,
      (metaJson as { workspaceDetail?: unknown }).workspaceDetail,
      (metaJson as { workspace?: unknown }).workspace,
    ]

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue

      const name = (candidate as { name?: unknown }).name
      if (typeof name === 'string' && name.trim().length > 0) {
        return name
      }
    }

    return null
  }

  function buildActiveDownloadMetadata(input: PendingDownloadInput): ActiveDownloadMetadata {
    return {
      workspaceName: input.display_name ?? extractWorkspaceNameFromMeta(input.meta_json),
      styleId: input.style_id,
      photosIncluded: input.include_photos,
      photoCount: input.photo_jobs.length,
    }
  }

  function normalizePendingDownload(
    input: PendingDownloadInput,
    expectedOwnerUserId: number,
  ): PendingOfflineDownload {
    if (input.owner_user_id !== expectedOwnerUserId) {
      throw new Error('Offline pending plan belongs to a different account')
    }
    return {
      workspaceId: input.workspace_id,
      input,
      workspaceName: input.display_name ?? extractWorkspaceNameFromMeta(input.meta_json),
      styleId: input.style_id,
      photosIncluded: input.include_photos,
      photoCount: input.photo_jobs.length,
      paused: input.paused ?? false,
      lastError: input.last_error ?? null,
    }
  }

  function buildDownloadInputForRetry(input: PendingDownloadInput): PendingDownloadInput {
    const apiBaseUrl = input.api_base_url ?? getNativeOfflineApiBaseUrl()

    return {
      ...input,
      ...(apiBaseUrl ? { api_base_url: apiBaseUrl } : {}),
      paused: false,
      last_error: null,
    }
  }

  function computePendingDownloadOverallTotal(input: PendingDownloadInput): number {
    const tileTotal = input.tile_sources.reduce((sum, source) => sum + source.tiles.length, 0)
    const photoTotal = input.include_photos ? input.photo_jobs.length : 0

    return Math.max(1, 1 + tileTotal + photoTotal)
  }

  async function loadPendingDownloads(
    expectedOwnerUserId: number,
  ): Promise<Record<string, PendingOfflineDownload>> {
    const response = await invoke<PendingDownloadsResponse>('list_pending_offline_downloads')
    const responseOwnerUserId = response.ownerUserId ?? response.owner_user_id
    if (responseOwnerUserId !== expectedOwnerUserId) {
      throw new Error('Offline pending plans belong to a different account')
    }

    return Object.fromEntries(
      (response.downloads ?? []).map((input) => {
        const normalized = normalizePendingDownload(input, expectedOwnerUserId)
        return [normalized.workspaceId, normalized]
      }),
    )
  }

  function applyPendingSnapshot(snapshot: PendingDownloadSnapshot): void {
    if (snapshot.epoch !== pendingEpoch) return
    const nextPending = { ...snapshot.downloads }
    for (const workspaceId of locallyChangedPendingIds) {
      const current = pending.value[workspaceId]
      if (current) {
        nextPending[workspaceId] = current
      } else {
        delete nextPending[workspaceId]
      }
    }
    pending.value = nextPending
    pendingHydrated = true
  }

  function upsertPendingDownload(workspaceId: string, nextPending: PendingOfflineDownload): void {
    pending.value[workspaceId] = nextPending
    locallyChangedPendingIds.add(workspaceId)
  }

  function removePendingDownload(workspaceId: string): void {
    delete pending.value[workspaceId]
    locallyChangedPendingIds.add(workspaceId)
  }

  function loadPendingSnapshot(expectedOwnerUserId: number): Promise<PendingDownloadSnapshot> {
    if (!pendingLoadPromise) {
      const epoch = pendingEpoch
      const request = loadPendingDownloads(expectedOwnerUserId).then((downloads) => ({
        epoch,
        downloads,
      }))
      pendingLoadPromise = request
      const clearRequest = () => {
        if (pendingLoadPromise === request) pendingLoadPromise = null
      }
      void request.then(clearRequest, clearRequest)
    }
    return pendingLoadPromise
  }

  async function hydratePendingDownloads(expectedOwnerUserId: number): Promise<void> {
    if (pendingHydrated) return
    const snapshot = await loadPendingSnapshot(expectedOwnerUserId)
    if (ownerUserId.value !== expectedOwnerUserId) return
    applyPendingSnapshot(snapshot)
  }

  async function refreshPendingDownloads(): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return

    try {
      const snapshot = await loadPendingSnapshot(expectedOwnerUserId)
      if (ownerUserId.value !== expectedOwnerUserId) return
      applyPendingSnapshot(snapshot)
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to refresh pending offline downloads', {
        error,
      })
    }
  }

  function getActiveDownloadMetadata(
    download?: ActiveDownload,
  ): ActiveDownloadMetadata | undefined {
    if (!download) return undefined

    return {
      workspaceName: download.workspaceName,
      styleId: download.styleId,
      photosIncluded: download.photosIncluded,
      photoCount: download.photoCount,
    }
  }

  function getOfflineErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error)
  }

  function normalizeOfflineEntry(
    entry: OfflineEntryPayload,
    expectedOwnerUserId: number,
  ): OfflineEntry | null {
    const workspaceId = entry.workspaceId ?? entry.workspace_id
    if (!workspaceId) return null
    const entryOwnerUserId = entry.ownerUserId ?? entry.owner_user_id
    if (entryOwnerUserId !== expectedOwnerUserId) return null

    const sourceIds = entry.sourceIds ?? entry.source_ids ?? []
    const styleId = entry.styleId ?? entry.style_id ?? ''
    const storedStatus = entry.status ?? 'ready'
    const status =
      storedStatus === 'ready' && styleId === 'routecamp' && !sourceIds.includes('base')
        ? 'partial'
        : storedStatus

    return {
      workspaceId,
      packageKind: entry.packageKind ?? entry.package_kind ?? 'workspace',
      workspaceName: entry.workspaceName ?? entry.workspace_name ?? null,
      coverageCells: normalizeCoverageCells(entry.coverageCells ?? entry.coverage_cells ?? []),
      includeTerrain: entry.includeTerrain ?? entry.include_terrain ?? false,
      sourceIds,
      sourceExtensions: entry.sourceExtensions ?? entry.source_extensions ?? {},
      sourceMinzooms: entry.sourceMinzooms ?? entry.source_minzooms ?? {},
      sourceMaxzooms: entry.sourceMaxzooms ?? entry.source_maxzooms ?? {},
      status,
      downloadedAt: entry.downloadedAt ?? entry.downloaded_at ?? 0,
      sizeBytes: entry.sizeBytes ?? entry.size_bytes ?? 0,
      tileCount: entry.tileCount ?? entry.tile_count ?? 0,
      photoCount: entry.photoCount ?? entry.photo_count ?? 0,
      photosIncluded: entry.photosIncluded ?? entry.photos_included ?? false,
      styleId,
      workspaceHash: entry.workspaceHash ?? entry.workspace_hash ?? '',
    }
  }

  function normalizeOfflineIndex(
    payload: OfflineIndexPayload,
    expectedOwnerUserId: number,
  ): Record<string, OfflineEntry> {
    const payloadOwnerUserId = payload.ownerUserId ?? payload.owner_user_id
    if (payloadOwnerUserId !== expectedOwnerUserId) {
      throw new Error('Offline index belongs to a different account')
    }
    const normalized: Record<string, OfflineEntry> = {}

    for (const rawEntry of Object.values(payload.workspaces ?? {})) {
      const entry = normalizeOfflineEntry(rawEntry, expectedOwnerUserId)
      if (!entry) continue
      normalized[entry.workspaceId] = entry
    }

    return normalized
  }

  function isCancelledOfflineError(error: unknown): boolean {
    return getOfflineErrorMessage(error).toLowerCase().includes('download cancelled')
  }

  async function startOfflineDownload(
    workspaceId: string,
    input: PendingDownloadInput,
    overallTotal: number,
  ): Promise<void> {
    if (!connectivityService.isEffectivelyOnline.value) {
      throw new Error('Connect to the internet to download offline data')
    }
    const expectedOwnerUserId = ownerUserId.value
    if (expectedOwnerUserId === null || input.owner_user_id !== expectedOwnerUserId) {
      throw new Error('Cannot start an offline download for a different account')
    }
    const retryInput = buildDownloadInputForRetry(input)
    upsertPendingDownload(workspaceId, normalizePendingDownload(retryInput, expectedOwnerUserId))

    active.value[workspaceId] = buildActiveDownloadState('meta', 0, overallTotal, {
      metadata: buildActiveDownloadMetadata(input),
    })

    void invoke('download_offline_workspace', { input: retryInput }).catch((error) => {
      if (ownerUserId.value !== expectedOwnerUserId) return
      if (isCancelledOfflineError(error)) {
        delete active.value[workspaceId]

        const pendingDownload = pending.value[workspaceId]
        if (pendingDownload?.paused) {
          logService.info(LOG_CATEGORIES.OFFLINE, 'Offline download paused', { workspaceId })
          return
        }

        logService.info(LOG_CATEGORIES.OFFLINE, 'Offline download cancelled', { workspaceId })
        return
      }

      const errorMessage = getOfflineErrorMessage(error)
      const existing = active.value[workspaceId]
      if (existing) {
        active.value[workspaceId] = {
          ...existing,
          error: errorMessage,
        }
      }

      const pendingDownload = pending.value[workspaceId]
      if (pendingDownload) {
        upsertPendingDownload(workspaceId, {
          ...pendingDownload,
          lastError: errorMessage,
          paused: false,
        })
      }

      logService.error(LOG_CATEGORIES.OFFLINE, 'Offline download failed', {
        workspaceId,
        error,
      })
    })
  }

  async function resumePendingDownloads(): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (
      !isTauri() ||
      expectedOwnerUserId === null ||
      !connectivityService.isEffectivelyOnline.value
    )
      return

    try {
      await hydratePendingDownloads(expectedOwnerUserId)
      if (
        ownerUserId.value !== expectedOwnerUserId ||
        !connectivityService.isEffectivelyOnline.value
      )
        return
      for (const pendingDownload of Object.values(pending.value)) {
        if (
          ownerUserId.value !== expectedOwnerUserId ||
          !connectivityService.isEffectivelyOnline.value
        )
          return
        const workspaceId = pendingDownload.workspaceId
        const existing = active.value[workspaceId]
        if (pendingDownload.paused) continue
        if (existing && !existing.error) continue

        const overallTotal = computePendingDownloadOverallTotal(pendingDownload.input)

        logService.info(LOG_CATEGORIES.OFFLINE, 'Resuming pending offline download', {
          workspaceId,
          tileTotal: pendingDownload.input.tile_sources.reduce(
            (sum, source) => sum + source.tiles.length,
            0,
          ),
          photoTotal: pendingDownload.input.include_photos
            ? pendingDownload.input.photo_jobs.length
            : 0,
        })

        await startOfflineDownload(workspaceId, pendingDownload.input, overallTotal)
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to resume pending offline downloads', {
        error,
      })
    }
  }

  async function registerOfflineListeners(): Promise<void> {
    if (listenersRegistered.value) return
    const registeredListeners: Array<() => void> = []

    try {
      registeredListeners.push(
        await listen<ProgressEvent>('offline:progress', (event) => {
          const { owner_user_id, workspace_id, phase, done, total } = event.payload
          if (owner_user_id !== ownerUserId.value) return
          const existing = active.value[workspace_id]
          active.value[workspace_id] = buildActiveDownloadState(phase, done, total, {
            error: existing?.error,
            metadata: getActiveDownloadMetadata(existing),
          })
        }),
      )

      registeredListeners.push(
        await listen<OfflineWorkspaceEvent>('offline:done', (event) => {
          const { owner_user_id, workspace_id: workspaceId } = event.payload
          if (owner_user_id !== ownerUserId.value) return
          delete active.value[workspaceId]
          removePendingDownload(workspaceId)
          refresh(workspaceId).catch((err) =>
            logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to refresh after download', { err }),
          )
        }),
      )

      registeredListeners.push(
        await listen<OfflineWorkspaceEvent>('offline:error', (event) => {
          const { owner_user_id, workspace_id: workspaceId } = event.payload
          if (owner_user_id !== ownerUserId.value) return
          const existing = active.value[workspaceId]
          if (existing) {
            active.value[workspaceId] = { ...existing, error: 'Download failed' }
          }
        }),
      )
      listenersRegistered.value = true
    } catch (error) {
      for (const unlisten of registeredListeners) {
        unlisten()
      }
      throw error
    }
  }

  async function init(): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || initialized.value || expectedOwnerUserId === null) return
    if (initializationPromise) return initializationPromise

    const expectedEpoch = pendingEpoch
    let operation: Promise<void> | null = null
    operation = (async () => {
      try {
        await registerOfflineListeners()
        const data = await invoke<OfflineIndexPayload>('list_offline_workspaces')
        const nextIndex = normalizeOfflineIndex(data, expectedOwnerUserId)
        if (ownerUserId.value !== expectedOwnerUserId || pendingEpoch !== expectedEpoch) return
        index.value = nextIndex
        initialized.value = true
      } catch (error) {
        logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to initialize offline store', { error })
      } finally {
        if (initializationPromise === operation) initializationPromise = null
      }
    })()
    initializationPromise = operation
    return operation
  }

  function resetForAccountChange(): void {
    ownerUserId.value = null
    index.value = {}
    active.value = {}
    pending.value = {}
    locallyChangedPendingIds.clear()
    pendingEpoch += 1
    pendingHydrated = false
    pendingLoadPromise = null
    initializationPromise = null
    deleting.value = {}
    deletePromises.clear()
    initialized.value = false
  }

  async function activateForOwner(nextOwnerUserId: number, initialize = true): Promise<void> {
    if (!Number.isSafeInteger(nextOwnerUserId) || nextOwnerUserId <= 0) {
      throw new Error('Invalid offline account owner')
    }
    if (ownerUserId.value === nextOwnerUserId) {
      if (initialize && !initialized.value) await init()
      return
    }

    index.value = {}
    active.value = {}
    pending.value = {}
    locallyChangedPendingIds.clear()
    pendingEpoch += 1
    pendingHydrated = false
    pendingLoadPromise = null
    initializationPromise = null
    deleting.value = {}
    deletePromises.clear()
    initialized.value = false
    ownerUserId.value = nextOwnerUserId
    if (initialize) await init()
  }

  async function refresh(workspaceId?: string): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return

    try {
      const data = await invoke<OfflineIndexPayload>('list_offline_workspaces')
      const normalized = normalizeOfflineIndex(data, expectedOwnerUserId)
      if (ownerUserId.value !== expectedOwnerUserId) return
      if (workspaceId) {
        if (normalized[workspaceId]) {
          index.value[workspaceId] = normalized[workspaceId]
        } else {
          delete index.value[workspaceId]
        }
      } else {
        index.value = normalized
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to refresh offline index', { error })
    }
  }

  async function refreshOverview(): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return

    try {
      if (deletePromises.size > 0) {
        await Promise.allSettled(Array.from(deletePromises.values()))
        if (ownerUserId.value !== expectedOwnerUserId) return
      }
      const [indexPayload, pendingSnapshot, tileCachePayload] = await Promise.all([
        invoke<OfflineIndexPayload>('list_offline_workspaces'),
        loadPendingSnapshot(expectedOwnerUserId),
        invoke<TileCacheStatsPayload>('get_tile_http_cache_size'),
      ])
      const nextIndex = normalizeOfflineIndex(indexPayload, expectedOwnerUserId)
      if (ownerUserId.value !== expectedOwnerUserId) return

      index.value = nextIndex
      applyPendingSnapshot(pendingSnapshot)
      tileCacheBytes.value = tileCachePayload.bytes ?? 0
      tileCacheEntries.value = tileCachePayload.entries ?? 0
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to refresh offline data overview', { error })
      throw error
    }
  }

  async function refreshSize(workspaceId: string): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return

    const entry = index.value[workspaceId]
    if (!entry) return

    try {
      const payload = await invoke<SizeStatsPayload>('get_offline_size', { workspaceId })
      if (ownerUserId.value !== expectedOwnerUserId) return
      const stats = normalizeSizeStats(payload)
      index.value[workspaceId] = {
        ...entry,
        sizeBytes: stats.sizeBytes,
        tileCount: stats.tileCount,
        photoCount: stats.photoCount,
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to refresh offline size stats', {
        workspaceId,
        error,
      })
      throw error
    }
  }

  async function refreshAllSizes(workspaceIds = Object.keys(index.value)): Promise<void> {
    if (!isTauri() || workspaceIds.length === 0) return

    const results = await Promise.allSettled(
      workspaceIds.map((workspaceId) => refreshSize(workspaceId)),
    )
    const failedCount = results.filter((result) => result.status === 'rejected').length

    if (failedCount > 0) {
      logService.warn(LOG_CATEGORIES.OFFLINE, 'Some offline size stats failed to refresh', {
        failedCount,
        totalCount: workspaceIds.length,
      })
    }
  }

  async function refreshTileCacheSize(): Promise<void> {
    if (!isTauri()) return

    try {
      const payload = await invoke<TileCacheStatsPayload>('get_tile_http_cache_size')
      tileCacheBytes.value = payload.bytes ?? 0
      tileCacheEntries.value = payload.entries ?? 0
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to read tile cache size', { error })
    }
  }

  async function clearTileCache(): Promise<void> {
    if (!isTauri()) return

    try {
      const payload = await invoke<TileCacheStatsPayload>('clear_tile_http_cache')
      tileCacheBytes.value = payload.bytes ?? 0
      tileCacheEntries.value = payload.entries ?? 0
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to clear tile cache', { error })
      throw error
    }
  }

  function computeWorkspaceHash(
    workspace: Workspace,
    tours: Tour[],
    pois: WorkspacePOI[] = [],
  ): string {
    const workspaceSignature = [
      workspace.clientId,
      workspace.name,
      workspace.description ?? '',
      String(workspace.isPublic ?? false),
      String(workspace.markedForDeletion ?? false),
      workspace.deletionScheduledAt ?? '',
    ].join('|')

    const tourSignature = tours
      .map((tour) => {
        const routePointSignature = tour.routePoints
          .map(
            (point) =>
              `${point.clientId}:${point.lat}:${point.lon}:${point.order}:${point.trackClientId}`,
          )
          .join(',')

        const trackSignature = tour.tracks
          .map((track) => {
            const segmentSignature = track.segments
              .map((segment) => {
                const pointSignature = segment.points
                  .map((point) => `${point.clientId}:${point.lat}:${point.lon}:${point.ele ?? ''}`)
                  .join(',')

                return [
                  segment.clientId ?? '',
                  segment.profile ?? '',
                  segment.creator ?? '',
                  pointSignature,
                ].join('~')
              })
              .join(',')

            return [
              track.clientId,
              track.name ?? '',
              track.color ?? '',
              track.routingProfile ?? '',
              track.type ?? '',
              segmentSignature,
            ].join('~')
          })
          .join('||')

        const waypointSignature = tour.waypoints
          .map((waypoint) =>
            [
              waypoint.clientId,
              waypoint.name,
              waypoint.type,
              waypoint.lat,
              waypoint.lon,
              waypoint.ele ?? '',
              waypoint.description ?? '',
            ].join('~'),
          )
          .join('||')

        return [
          tour.clientId,
          tour.name,
          tour.description ?? '',
          tour.routingProfile ?? '',
          routePointSignature,
          trackSignature,
          waypointSignature,
        ].join('###')
      })
      .join('@@@')

    const poiSignature = pois
      .map((poi) =>
        [
          poi.clientId,
          poi.workspaceClientId,
          poi.name ?? '',
          poi.description ?? '',
          poi.type ?? '',
          poi.lat,
          poi.lon,
          poi.ele ?? '',
          poi.time ?? '',
        ].join('~'),
      )
      .join('@@@')

    return `${workspaceSignature}::${tourSignature}::${poiSignature}`
  }

  function getNativeOfflineApiBaseUrl(): string | undefined {
    const explicitNativeOrigin = import.meta.env.VITE_NATIVE_API_ORIGIN?.trim()
    if (explicitNativeOrigin) return explicitNativeOrigin

    const apiBaseUrl = resolveApiBaseUrl()
    return apiBaseUrl && /^https?:\/\//i.test(apiBaseUrl) ? apiBaseUrl : undefined
  }

  function photoAssetPath(photoId: string, thumbnail = false): string {
    return `workspaces/photos/${photoId}/${thumbnail ? 'thumbnail' : 'file'}`
  }

  function buildWorkspaceSearchItem(
    workspace: Workspace,
    tours: Tour[],
    photos: Photo[],
  ): WorkspaceSearchItem {
    return {
      clientId: workspace.clientId,
      name: workspace.name,
      description: workspace.description ?? null,
      isPublic: workspace.isPublic ?? false,
      isOwner: workspace.userRole === 'Owner',
      ownerNames: workspace.ownerNames ?? null,
      photoCount: photos.length,
      type: 1,
      tours: tours.map((tour) => ({
        clientId: tour.clientId,
        name: tour.name,
        tracks: tour.tracks.map((track) => ({
          clientId: track.clientId,
          name: track.name,
          pointsLineReduced500m:
            track.pointsLineReduced500m ??
            track.pointsLineReduced5m ??
            buildLineStringFromSegments(track.segments),
          startTime: track.startTime,
          endTime: track.endTime,
        })),
      })),
    }
  }

  function buildLibraryTours(tours: Tour[], workspace: Workspace): TourListItem[] {
    return tours.map((tour) => ({
      clientId: tour.clientId,
      name: tour.name,
      description: tour.description ?? null,
      type: tour.type ?? null,
      distance: tour.distance ?? null,
      elevationGain: tour.elevationGain ?? null,
      startDate: null,
      endDate: null,
      workspaces: [{ clientId: workspace.clientId, name: workspace.name }],
      tracks: tour.tracks.map((track) => ({
        clientId: track.clientId,
        pointsLineReduced500m:
          track.pointsLineReduced500m ??
          track.pointsLineReduced5m ??
          buildLineStringFromSegments(track.segments),
        color: track.color,
      })),
    }))
  }

  async function buildOfflineGeometryBundle(
    workspaceId: string,
    tours: Tour[],
  ): Promise<Partial<Record<'50m' | '5m', OfflineGeometryBundle>>> {
    const geometryByResolution: Partial<Record<'50m' | '5m', OfflineGeometryBundle>> = {}
    const tourIds = tours.map((tour) => tour.clientId)

    for (const resolution of ['50m', '5m'] as const) {
      try {
        const [workspaceResults, tourResults] = await Promise.all([
          apiService.getWorkspaceTracksGeometry([workspaceId], resolution),
          tourIds.length > 0
            ? apiService.getTourTracksGeometry(tourIds, resolution)
            : Promise.resolve([]),
        ])

        const workspaceGeometry = workspaceResults.find((item) => item.clientId === workspaceId)
        geometryByResolution[resolution] = {
          workspaceTracksGeometry: workspaceGeometry?.tracksGeometry ?? [],
          tourTracksGeometry: Object.fromEntries(
            tourResults.map((item) => [item.clientId, item.tracksGeometry]),
          ),
        }
      } catch (error) {
        logService.warn(LOG_CATEGORIES.OFFLINE, 'Failed to fetch offline geometry bundle', {
          workspaceId,
          resolution,
          error,
        })
      }
    }

    return geometryByResolution
  }

  async function listOfflineWorkspaceCatalog(): Promise<WorkspaceSearchItem[]> {
    const snapshots = await Promise.all(
      readyWorkspaceIds.value.map(async (workspaceId) => {
        const snapshot = await readWorkspaceSnapshot(workspaceId)
        return snapshot?.searchItem ?? null
      }),
    )

    return snapshots.filter((item): item is WorkspaceSearchItem => !!item)
  }

  async function listOfflineLibraryTours(): Promise<TourListItem[]> {
    const snapshots = await Promise.all(
      readyWorkspaceIds.value.map(async (workspaceId) => readWorkspaceSnapshot(workspaceId)),
    )

    return snapshots.flatMap((snapshot) => snapshot?.libraryTours ?? [])
  }

  async function findOfflineTourDetail(tourId: string): Promise<Tour | null> {
    for (const workspaceId of readyWorkspaceIds.value) {
      const snapshot = await readWorkspaceSnapshot(workspaceId)
      const match = snapshot?.workspaceDetail.tours.find((tour) => tour.clientId === tourId)
      if (match) {
        return {
          ...match,
          _detailsLoaded: true,
        }
      }
    }

    return null
  }

  async function makeOffline(
    workspace: Workspace,
    tours: Tour[],
    photos: Photo[],
    pois: WorkspacePOI[],
    preference: MapStylePreference,
    options: OfflineDownloadOptions,
  ): Promise<void> {
    if (!isTauri()) return
    const downloadOwnerUserId = ownerUserId.value
    if (downloadOwnerUserId === null) {
      throw new Error('Sign in to download offline data')
    }

    const workspaceId = workspace.clientId
    const { includePhotos, includeTerrain } = options

    settingsService.saveSetting(UI_SETTING_KEYS.offlineIncludePhotos, includePhotos)
    settingsService.saveSetting(UI_SETTING_KEYS.offlineIncludeTerrain, includeTerrain)

    try {
      const bbox = computeWorkspaceBbox(tours, pois)
      if (!bbox && (tours.some((t) => t.tracks.length > 0) || pois.length > 0)) {
        logService.warn(LOG_CATEGORIES.OFFLINE, 'Could not compute bbox — no content coordinates', {
          workspaceId,
        })
      }

      const sources = await discoverTileSources(preference)
      const tiles = bbox ? enumerateTiles(bbox, OFFLINE_MAX_ZOOM) : []

      const tileSources = selectTileSourcesForDownload(sources, { includeTerrain }).map((src) => ({
        source_id: src.sourceId,
        tiles: buildTileJobs(tiles, src),
      }))
      const tileTotal = tileSources.reduce((sum, source) => sum + source.tiles.length, 0)

      const workspaceDetail: WorkspaceDetailPayload = {
        clientId: workspace.clientId,
        name: workspace.name,
        description: workspace.description ?? null,
        isPublic: workspace.isPublic ?? false,
        userRole: workspace.userRole ?? null,
        canEdit: workspace.canEdit ?? null,
        canManage: workspace.canManage ?? null,
        canView: workspace.canView ?? null,
        ownerNames: workspace.ownerNames ?? null,
        markedForDeletion: workspace.markedForDeletion ?? false,
        deletionScheduledAt: workspace.deletionScheduledAt ?? null,
        tours,
        pois,
        photos: photos
          .filter((photo) => Boolean(photo.clientId))
          .map((photo) => ({
            clientId: photo.clientId,
            name: photo.name,
            hasCoordinates: photo.hasCoordinates,
            coordinateSource: photo.coordinateSource,
            lat: photo.lat ?? null,
            lon: photo.lon ?? null,
            takenAt: photo.takenAt ?? null,
            url: photoAssetPath(photo.clientId),
            thumbnailUrl: photoAssetPath(photo.clientId, true),
            canEdit: photo.canEdit ?? null,
            uploadedByName: photo.uploadedByName ?? null,
          })),
      }
      const searchItem = buildWorkspaceSearchItem(workspace, tours, photos)
      const libraryTours = buildLibraryTours(tours, workspace)
      const geometryByResolution = await buildOfflineGeometryBundle(workspaceId, tours)
      if (!geometryByResolution['50m'] || !geometryByResolution['5m']) {
        throw new Error('Offline package is incomplete: failed to fetch required track geometry')
      }

      const photoJobs = includePhotos
        ? photos
            .filter((p) => p._thumbnailUrl && p._binaryDataUrl)
            .map((p) => ({
              photo_id: p.clientId,
              url: photoAssetPath(p.clientId),
              thumbnail_url: photoAssetPath(p.clientId, true),
              ext: 'jpg',
            }))
        : []
      const photoTotal = photoJobs.length
      const overallTotal = Math.max(1, 1 + tileTotal + photoTotal)

      const workspaceHash = computeWorkspaceHash(workspace, tours, pois)
      const styleCacheKey = getOfflineStyleCacheKey(preference)
      await startOfflineDownload(
        workspaceId,
        {
          owner_user_id: downloadOwnerUserId,
          workspace_id: workspaceId,
          package_kind: 'workspace',
          display_name: workspace.name,
          coverage_cells: [],
          include_terrain: includeTerrain,
          style_id: styleCacheKey,
          workspace_hash: workspaceHash,
          api_base_url: getNativeOfflineApiBaseUrl(),
          meta_json: {
            version: 3,
            workspace_detail: workspaceDetail,
            search_item: searchItem,
            library_tours: libraryTours,
            geometry_by_resolution: geometryByResolution,
          },
          tile_sources: tileSources,
          photo_jobs: photoJobs,
          include_photos: includePhotos,
        },
        overallTotal,
      )
    } catch (error) {
      if (ownerUserId.value !== downloadOwnerUserId) return
      const existing = active.value[workspaceId]
      if (existing) {
        active.value[workspaceId] = {
          ...existing,
          error: getOfflineErrorMessage(error),
        }
      }
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to start offline download', {
        workspaceId,
        error,
      })
      throw error
    }
  }

  async function discoverManualMapSources(
    preference: MapStylePreference,
  ): Promise<TileSourceInfo[]> {
    if (preference.styleId !== 'routecamp') {
      throw new Error('Manual map areas currently use the Routecamp map style')
    }
    const sources = await discoverTileSources(preference)
    const manualMapSources = sources.filter(
      (source) => source.sourceId === 'base' || source.sourceId === 'hillshade',
    )
    if (
      !manualMapSources.some(
        (source) => source.category === 'mapData' && source.sourceId === 'base',
      )
    ) {
      throw new Error('The selected map style does not provide downloadable map tiles')
    }
    return manualMapSources
  }

  function estimateManualMapSize(
    coverageCells: OfflineCoverageCell[],
    sources: TileSourceInfo[],
  ): OfflineEstimate {
    return estimateCoverageSize(normalizeCoverageCells(coverageCells), sources, OFFLINE_MAX_ZOOM)
  }

  async function makeManualMapOffline(
    coverageCells: OfflineCoverageCell[],
    preference: MapStylePreference,
    includeTerrain: boolean,
    preparedSources?: TileSourceInfo[],
  ): Promise<void> {
    if (!isTauri()) throw new Error('Offline map areas are available in the native apps')
    if (!connectivityService.isEffectivelyOnline.value) {
      throw new Error('Connect to the internet to download map areas')
    }
    const downloadOwnerUserId = ownerUserId.value
    if (downloadOwnerUserId === null) {
      throw new Error('Sign in to download offline map areas')
    }

    const normalizedCoverage = normalizeCoverageCells(coverageCells)
    if (normalizedCoverage.length === 0) {
      await deleteOffline(MANUAL_MAP_PACKAGE_ID)
      return
    }

    const sources = preparedSources ?? (await discoverManualMapSources(preference))
    if (!sources.some((source) => source.category === 'mapData' && source.sourceId === 'base')) {
      throw new Error('Downloadable Routecamp map tiles could not be prepared')
    }
    if (includeTerrain && !sources.some((source) => source.category === 'terrain')) {
      throw new Error('Contours and terrain tiles could not be prepared')
    }
    const selectedSources = selectTileSourcesForDownload(sources, { includeTerrain })
    const estimate = estimateCoverageSize(normalizedCoverage, selectedSources, OFFLINE_MAX_ZOOM)
    assertManualMapEstimateWithinLimits(estimate)
    const tiles = enumerateCoverageTiles(
      normalizedCoverage,
      OFFLINE_MAX_ZOOM,
      MANUAL_MAP_MAX_TILE_JOBS,
    )
    const tileSources = selectedSources.map((source) => ({
      source_id: source.sourceId,
      tiles: buildTileJobs(tiles, source),
    }))
    const tileTotal = tileSources.reduce((sum, source) => sum + source.tiles.length, 0)
    const styleCacheKey = getOfflineStyleCacheKey(preference)
    const workspaceHash = JSON.stringify({
      coverage: normalizedCoverage,
      includeTerrain,
      styleId: styleCacheKey,
    })

    settingsService.saveSetting(UI_SETTING_KEYS.offlineIncludeTerrain, includeTerrain)
    await startOfflineDownload(
      MANUAL_MAP_PACKAGE_ID,
      {
        owner_user_id: downloadOwnerUserId,
        workspace_id: MANUAL_MAP_PACKAGE_ID,
        package_kind: 'map_area',
        display_name: MANUAL_MAP_DISPLAY_NAME,
        coverage_cells: normalizedCoverage,
        include_terrain: includeTerrain,
        style_id: styleCacheKey,
        workspace_hash: workspaceHash,
        api_base_url: getNativeOfflineApiBaseUrl(),
        meta_json: {
          version: 1,
          package_kind: 'map_area',
          coverage_cells: normalizedCoverage,
          include_terrain: includeTerrain,
        },
        tile_sources: tileSources,
        photo_jobs: [],
        include_photos: false,
      },
      Math.max(1, 1 + tileTotal),
    )
  }

  async function deleteOffline(workspaceId: string): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return
    const existingDelete = deletePromises.get(workspaceId)
    if (existingDelete) return existingDelete

    deleting.value[workspaceId] = true
    const operation = (async () => {
      try {
        if (active.value[workspaceId]) {
          await pauseDownload(workspaceId)
          if (ownerUserId.value !== expectedOwnerUserId) return
        }

        await invoke('delete_offline_workspace', { workspaceId })
        if (ownerUserId.value !== expectedOwnerUserId) return
        delete index.value[workspaceId]
        removePendingDownload(workspaceId)
        delete active.value[workspaceId]
      } catch (error) {
        logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to delete offline workspace', {
          workspaceId,
          error,
        })
        throw error
      } finally {
        delete deleting.value[workspaceId]
      }
    })()
    deletePromises.set(workspaceId, operation)
    const clearDeletePromise = () => {
      if (deletePromises.get(workspaceId) === operation) {
        deletePromises.delete(workspaceId)
      }
    }
    void operation.then(clearDeletePromise, clearDeletePromise)

    return operation
  }

  async function cancelDownload(workspaceId: string): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return

    try {
      await invoke('cancel_offline_download', { workspaceId })
      if (ownerUserId.value !== expectedOwnerUserId) return
      delete active.value[workspaceId]
      removePendingDownload(workspaceId)
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to cancel download', {
        workspaceId,
        error,
      })
    }
  }

  async function pauseDownload(workspaceId: string): Promise<void> {
    const expectedOwnerUserId = ownerUserId.value
    if (!isTauri() || expectedOwnerUserId === null) return

    try {
      await invoke('pause_offline_download', { workspaceId })
      if (ownerUserId.value !== expectedOwnerUserId) return

      const existing = pending.value[workspaceId]
      if (existing) {
        upsertPendingDownload(workspaceId, {
          ...existing,
          paused: true,
        })
      }
    } catch (error) {
      logService.error(LOG_CATEGORIES.OFFLINE, 'Failed to pause download', {
        workspaceId,
        error,
      })
    }
  }

  async function resumeDownload(workspaceId: string): Promise<void> {
    if (!isTauri() || !connectivityService.isEffectivelyOnline.value) return

    const existing = pending.value[workspaceId]
    if (!existing) return

    const activeDownload = active.value[workspaceId]
    if (activeDownload && !activeDownload.error) return

    await startOfflineDownload(
      workspaceId,
      existing.input,
      computePendingDownloadOverallTotal(existing.input),
    )
  }

  function getSavedIncludePhotos(): boolean {
    return settingsService.getSetting<boolean>(UI_SETTING_KEYS.offlineIncludePhotos) ?? false
  }

  function getSavedIncludeTerrain(): boolean {
    return settingsService.getSetting<boolean>(UI_SETTING_KEYS.offlineIncludeTerrain) ?? true
  }

  return {
    index,
    active,
    pending,
    deleting,
    initialized,
    isOffline,
    needsUpdate,
    needsStyleUpdate,
    totalSize,
    manualMapEntry,
    ownerUserId,
    getEntry,
    getStyleCacheKey,
    readWorkspaceSnapshot,
    listOfflineWorkspaceCatalog,
    listOfflineLibraryTours,
    findOfflineTourDetail,
    init,
    activateForOwner,
    resetForAccountChange,
    refresh,
    refreshOverview,
    refreshPendingDownloads,
    refreshSize,
    refreshAllSizes,
    tileCacheBytes,
    tileCacheEntries,
    refreshTileCacheSize,
    clearTileCache,
    resumePendingDownloads,
    makeOffline,
    discoverManualMapSources,
    estimateManualMapSize,
    makeManualMapOffline,
    deleteOffline,
    cancelDownload,
    pauseDownload,
    resumeDownload,
    getSavedIncludePhotos,
    getSavedIncludeTerrain,
    computeWorkspaceHash,
    estimateOfflineSize: (tours: Tour[], pois: WorkspacePOI[], preference: MapStylePreference) =>
      estimateOfflineSize(tours, preference, OFFLINE_MAX_ZOOM, pois),
    LARGE_DOWNLOAD_BYTES_THRESHOLD,
  }
})
